import React, { useCallback } from 'react'
import { Anchor, Button } from '@mantine/core'
import { useAtomValue } from 'jotai'

import { StepperModalContent, useStepperContext } from '@/components'
import { useLatestValue, useMount } from '@/hooks'
import { notify } from '@/contexts/notifications'
import { useSelectedDenom } from '../../../contexts/denom'
import { StandardTransactionError, formatMicroDenom } from '@/wallet-utils'
import { CHAIN_INFO_LIST } from '@/config'
import { AnalyticsTracker } from '@/page-components/shared'
import { withdrawModalIsOpenAtom } from '../atoms'
import { useWithdrawModal } from './WithdrawModalProvider'

const WithdrawStepTwo: React.FC = () => {
  const { complete, forceClose, close, handleClose } = useStepperContext()

  const {
    withdrawAmount,
    broadcastWithdraw,
    isBroadcastingWithdraw,
    isBroadcastWithdrawSuccessful,
    broadcastWithdrawError,
    traceIbcStatus,
    isTracingIbcStatus
  } = useWithdrawModal()

  const [denom] = useSelectedDenom()

  const isOpen = useAtomValue(withdrawModalIsOpenAtom)

  const isOpenRef = useLatestValue(isOpen)

  const chainInfo = CHAIN_INFO_LIST[denom]

  const formattedAmount = `${formatMicroDenom(withdrawAmount, chainInfo.stakeCurrency.coinMinimalDenom, 3)} ${denom}`

  const handleStep = useCallback(async () => {
    const { status } = await broadcastWithdraw()

    if (!isOpenRef.current) {
      // This function doesn't get cancelled when the modal gets closed.
      // If we get here when pool is already closed, it's likely that the stuff that
      // `traceIbcStatus` (transactionRef) needs has already been reset by the
      // modal close handler.
      return
    }

    if (status === 'return-later') {
      // If we're here, it means that our ibc transfer took too long and we're tracking it
      // the second time without the 30-second timeout. This way, the user can continue the
      // flow if it they haven't closed.
      //
      // We can check for the return-later status by checking using `isIbcStatusLoading`
      // to check if the ibc transfer hasn't completed and ibc tracking is still on-going.
      //
      // @TODO: Handle timeout state if this doesn't succeed. The original implementation
      // from the ibc/staking modal doesn't have this yet neither. Add display states for
      // `status === 'timeout'` for both broadcast and tracing and `ibcStatusError`
      await traceIbcStatus()
    }

    complete()
  }, [isOpenRef, broadcastWithdraw, traceIbcStatus, complete])

  const handleCloseCallback = useCallback(() => {
    // If it was closed after the broadcast, it doesn't matter
    if (!isBroadcastingWithdraw && !isTracingIbcStatus) {
      return
    }

    notify.progress('Transaction minimized', "We'll let you know when the withdraw completes.")
  }, [isBroadcastingWithdraw, isTracingIbcStatus])

  useMount(() => {
    handleStep()
  })

  handleClose(handleCloseCallback)

  const handleRetrySignWithdrawToken = useCallback(async () => {
    handleStep()
  }, [handleStep])

  if (isTracingIbcStatus) {
    return (
      <StepperModalContent
        title="This transaction is taking longer than usual"
        description={`If you don't want to wait here, you can close this dialog. Your transaction will continue and you’ll be able to see status on the Stride app page.`}
        actions={
          <>
            <Button color="dark" onClick={forceClose}>
              Exit
            </Button>
          </>
        }
      />
    )
  }

  if (
    broadcastWithdrawError instanceof StandardTransactionError &&
    broadcastWithdrawError.description === 'INSUFFICIENT_FUNDS'
  ) {
    return (
      <StepperModalContent
        title="Transaction error"
        description={
          <>
            Insufficient funds for gas. Get STRD, ATOM or TIA on{' '}
            <Anchor href="https://app.osmosis.zone/?from=ATOM&to=OSMO" target="_blank">
              Osmosis
            </Anchor>
            .
          </>
        }
        actions={
          <>
            <Button color="dark" onClick={forceClose}>
              Exit
            </Button>
          </>
        }
        error={broadcastWithdrawError}
      />
    )
  }

  if (broadcastWithdrawError instanceof StandardTransactionError) {
    return (
      <StepperModalContent
        title="Transaction error"
        description={broadcastWithdrawError.message}
        actions={
          <>
            <Button color="dark" onClick={forceClose}>
              Exit
            </Button>

            <Button color="dark" variant="outline" onClick={handleRetrySignWithdrawToken}>
              Try again
            </Button>
          </>
        }
        error={broadcastWithdrawError}
      />
    )
  }

  if (broadcastWithdrawError) {
    return (
      <StepperModalContent
        title="Transaction error"
        description="This withdraw could not be completed. Your tokens have not been moved."
        actions={
          <>
            <Button color="dark" onClick={forceClose}>
              Exit
            </Button>

            <Button color="dark" variant="outline" onClick={handleRetrySignWithdrawToken}>
              Try again
            </Button>
          </>
        }
      />
    )
  }

  if (isBroadcastWithdrawSuccessful) {
    return (
      <>
        <StepperModalContent
          title="Success!"
          description={`${formattedAmount} have been added to your wallet`}
          actions={<Button onClick={close}>Done</Button>}
        />

        <AnalyticsTracker eventName="withdraw_token_complete" />
      </>
    )
  }

  return (
    <StepperModalContent
      title={`Transferring ${denom} to your wallet...`}
      description="This could take 30 seconds or longer if the network is congested. If you exit Stride, this status may not be visible when you return, but the transfer will continue. Once the transfer is complete, you will need to return to initiate the staking process."
    />
  )
}

export { WithdrawStepTwo }
